<template>
  <v-dialog
    :fullscreen="mdAndDown"
    v-model="isDialogOpen"
    max-width="850"
    :persistent="canChangeSettings"
  >
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        variant="outlined"
        color="primary"
      >
        <v-icon :start="!mdAndDown">mdi-cog</v-icon>
        <span v-if="!mdAndDown">{{ $t('topics.optimization.button') }}</span>
      </v-btn>
    </template>
    <template #default>
      <v-card :class="{ 'topic-settings__content--hidden': !canChangeSettings }">
        <v-card-title class="topic-settings__title">
          <h1 class="text-h6">{{ $t('topics.optimization.title') }}</h1>
          <v-btn
            variant="text"
            icon="mdi-close"
            color="white"
            @click="isDialogOpen = false"
          />
        </v-card-title>
        <BlurredBackdropUpselling :is-blurred="!canChangeSettings">
          <div
            :class="{
              'topic-settings__content': true,
              'topic-settings__content--padding-bottom': mdAndDown,
              'topic-settings__content--hidden': !canChangeSettings,
            }"
          >
            <h2 class="text-subtitle-1 mb-4">
              {{ $t('topics.optimization.subtitle') }}
            </h2>
            <v-form
              class="topic-settings__content-field"
              @submit.prevent="changeDomain"
              v-model="isDomainValid"
            >
              <p class="font-weight-bold mb-2">
                {{ $t('topics.optimization.website-data.title') }}
              </p>
              <p class="text-grey mb-0">
                {{ $t('topics.optimization.website-data.description1') }}
              </p>
              <p class="text-grey">
                {{ $t('topics.optimization.website-data.description2') }}
              </p>
              <CheckTextField
                v-model.trim="website"
                :label="$t('settings.company.website.labels.url-website')"
                placeholder="https://www.max-makler.de"
                :rules="emailRules"
                outlined
                validate-on-input
                :loading="loading || fieldsLoading.website"
              />
              <div class="topic-settings__content-actions">
                <v-btn
                  color="primary"
                  :loading="loading || fieldsLoading.website"
                  :disabled="!isDomainValid"
                  type="submit"
                  data-testid="topics-settings-scrape-website-submit"
                >
                  {{ $t('topics.optimization.website-data.button') }}
                </v-btn>
              </div>
            </v-form>
            <div class="topic-settings__content-field">
              <p class="font-weight-bold mb-2">
                {{ $t('topics.optimization.company-data.title') }}
              </p>
              <p class="text-grey">
                {{ $t('topics.optimization.company-data.description') }}
              </p>
              <v-autocomplete
                v-model="form.country"
                :items="availableCountryItems"
                item-title="text"
                item-value="value"
                :label="$t('settings.company.ai.country-field.label')"
                variant="outlined"
                :loading="loading || fieldsLoading.country"
                hide-details
                :placeholder="$t('product-finder.basic-info.country-placeholder')"
                class="pointer mb-6"
                data-testid="topics-settings-update-country"
                @blur="updateMetaData({ country: form.country })"
                :menu-props="{
                  attach: '#topic-settings-country',
                }"
              />
              <div id="topic-settings-country" />
              <CheckTextField
                :label="$t('settings.company.ai.industry-field.label')"
                :loading="loading || fieldsLoading.industry"
                v-model.trim="form.industry"
                data-testid="topics-settings-update-industry"
                @blur="updateMetaData({ industry: form.industry })"
              />
              <CheckTextField
                :label="$t('settings.company.address-data.company-name')"
                :placeholder="$t('settings.company.address-data.company-placeholder')"
                :loading="loading || fieldsLoading.name"
                v-model.trim="form.name"
                data-testid="topics-settings-update-company-name"
                @blur="updateCompanyData()"
              />
              <v-textarea
                :label="$t('settings.company.ai.businessDescription-field.label')"
                :placeholder="$t('product-finder.enter-description-placeholder')"
                :loading="loading || fieldsLoading.businessDescription"
                v-model.trim="form.businessDescription"
                variant="outlined"
                data-testid="topics-settings-update-business-description"
                @blur="updateMetaData({ businessDescription: form.businessDescription })"
              />

              <div class="topic-settings__content-actions">
                <v-btn
                  color="primary"
                  :loading="loading"
                  data-testid="topics-settings-generate-topic"
                  @click="createNewTopic"
                  >{{ $t('topics.optimization.company-data.button') }}</v-btn
                >
              </div>
            </div>
          </div>
        </BlurredBackdropUpselling>
      </v-card>
      <ConfirmationDialog
        ref="confirmationDialog"
        :title="$t('topics.optimization.confirmation.title')"
        :text="$t('topics.optimization.confirmation.text')"
      />
    </template>
  </v-dialog>
</template>
<script lang="ts" setup>
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'
import CheckTextField from '@/components/forms/CheckTextField.vue'
import CREATE_ANAMNESIS_FROM_WEBSITE from '@/components/product-finder/queries/createAnamnesisFromWebsite.gql'
import { CountryItem, useCountries } from '@/composables/useCountries'
import { ProductType } from '@/lib/productTypes'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { formatHttpsUrl } from '@/lib/urlFormatter'
import { url } from '@/lib/validation'
import COMPANY from '@/modules/settings/companySettings/queries/CompanyData.gql'
import UPDATE_COMPANY_META_DATA from '@/modules/settings/companySettings/queries/UpdateCompanyMetaData.gql'
import UPDATE_COMPANY_NAME from '@/modules/settings/companySettings/queries/UpdateCompanyName.gql'
import { TrackingPlugin } from '@/plugins/tracking'

const emit = defineEmits<{
  (e: 'create-topic'): void
}>()

const $auth = inject('auth')
const tracking = inject<TrackingPlugin>('tracking')
const { mdAndDown } = useDisplay()
const { availableCountryItems } = useCountries()
const { t } = useI18n()

const { onResult, result, load, loading } = useLazyQuery(COMPANY, {
  id: ($auth as any).user.companyId,
})

const emailRules = [url, (value: string) => !!value.length || t('labels.required')]

const {
  mutate: mutateMetaData,
  onDone: onDoneMetaData,
  onError: onErrorMetaData,
} = useMutation(UPDATE_COMPANY_META_DATA)
const {
  mutate: mutateCompanyData,
  onDone: onDoneCompanyData,
  onError: onErrorCompanyData,
} = useMutation(UPDATE_COMPANY_NAME)
const {
  mutate: mutateCreateAnamnesisFromWebsite,
  onDone: onDoneCreateAnamnesisFromWebsite,
  onError: onErrorCreateAnamnesisFromWebsite,
} = useMutation(CREATE_ANAMNESIS_FROM_WEBSITE)

const confirmationDialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null)

const isDialogOpen = ref(false)
const isDomainValid = ref(false)
const website = ref('')
const form = ref<{
  [key: string]: string | null
}>({
  country: null,
  industry: '',
  name: '',
  businessDescription: '',
})
const fieldsLoading = ref<Record<keyof typeof form.value, boolean> & { website: boolean }>({
  country: false,
  industry: false,
  name: false,
  businessDescription: false,
  website: false,
})

onResult(({ data = null }: { data: any }) => {
  if (!data?.company) return
  setDefaultFormValues(data.company)
})

const spawnSuccessSnackbar = () =>
  showSnackbarMessage('success', t('alerts.settings.company.success'))

const spawnErrorSnackbar = () => showSnackbarMessage('error', t('alerts.settings.company.error'))

onDoneMetaData(spawnSuccessSnackbar)
onDoneCompanyData(spawnSuccessSnackbar)
onDoneCreateAnamnesisFromWebsite(spawnSuccessSnackbar)

onErrorMetaData(spawnErrorSnackbar)
onErrorCompanyData(spawnErrorSnackbar)
onErrorCreateAnamnesisFromWebsite(spawnErrorSnackbar)

const canChangeSettings = computed(() => {
  // eslint-disable-next-line no-unsafe-optional-chaining
  const { productType } = ($auth as any)?.user
  if (!productType) return false
  const notSupportedAccountTypes = [
    ProductType.BEGINNER,
    ProductType.LIGHT,
    ProductType.BEGINNER_TRIAL,
  ]
  if (notSupportedAccountTypes.includes(productType)) return false
  return true
})

const setDefaultFormValues = (company: any) => {
  assignSelectedCountry()
  form.value.businessDescription = company.meta?.businessDescription
  form.value.industry = company.meta?.industry
  form.value.name = company.name
  website.value = company.links.website
}

const changeDomain = async () => {
  if (!(await confirmationDialog.value?.confirm())) return
  try {
    website.value = formatHttpsUrl(website.value)
    fieldsLoading.value.website = true
    const {
      data: { anamnesis },
    } = await mutateCreateAnamnesisFromWebsite({ url: website.value })
    tracking.event('Marketing Topics', 'Clicked', 'Scrape Website')
    if (anamnesis) {
      const {
        name,
        country,
        meta: { industry, businessDescription },
      } = anamnesis
      form.value.name = name
      form.value.industry = industry
      form.value.businessDescription = businessDescription
      form.value.country =
        availableCountryItems.value.find(
          (item: CountryItem) => item.value === country?.toUpperCase()
        )?.value ?? null
    }
  } finally {
    fieldsLoading.value.website = false
  }
}

const assignSelectedCountry = () => {
  if (!result.value) return
  form.value.country =
    availableCountryItems.value.find(
      (item: CountryItem) => item.value === result.value.company.country?.toUpperCase()
    )?.value ?? null
}
const updateCompanyData = async () => {
  try {
    fieldsLoading.value.name = true
    await mutateCompanyData({
      input: { companyId: ($auth as any).user.companyId, name: form.value.name },
    })
  } finally {
    fieldsLoading.value.name = false
  }
}
const updateMetaData = async (input: Record<keyof typeof form.value, string | null>) => {
  const [key] = Object.keys(input) as Array<keyof typeof form.value>
  try {
    fieldsLoading.value[key] = true
    await mutateMetaData({ input })
  } finally {
    fieldsLoading.value[key] = false
  }
}

const createNewTopic = () => {
  emit('create-topic')
  isDialogOpen.value = false
}

watch(isDialogOpen, async (isOpen: boolean) => {
  if (isOpen) {
    await load()
    tracking.event('Marketing Topics', 'Clicked', 'Topic Settings')
  }
  if (result.value?.company) setDefaultFormValues(result.value.company)
})
</script>
<style scoped>
.topic-settings__title {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  background-color: rgb(var(--v-theme-primary));
}
.topic-settings__content {
  padding: 8px 16px;
}
.topic-settings__content--hidden {
  overflow-y: hidden !important;
  height: 100%;
}
.topic-settings__content--padding-bottom {
  padding-bottom: 4rem;
}
.topic-settings__content-actions {
  display: flex;
  padding-bottom: 16px;
  justify-content: end;
}
</style>
